import { AuthContextProvider, ServiceEnvironment } from '@streamelements/client-sdk-js';
import { CompositionsService, VideoCompositionWithScenes } from '../../../services/CompositionsService';
import { DestinationMetadataApiClient, DestinationsStorageApiClient } from '../../../services/DestinationsApiClient';
import { promisefy } from '../../../utils/promisefy';
import { ensureOBSPresence } from '../../../utils/ensureOBSPresence';
import { ReplayBufferService } from '../../../services/ReplayBufferService';
import { getIsReplayBufferEnabled } from '../../../constants/common';

export type CompositionOrCompositionId = VideoCompositionWithScenes | string;

async function getComposition(input: CompositionOrCompositionId) {
  return typeof input === 'string' ? await CompositionsService.getCompositionFromMemory(input) : input;
}

export async function getOutputsUsingComposition(composition: VideoCompositionWithScenes) {
  const allOutputs = await promisefy(window.host.getAllStreamingOutputs)();

  const outputs = Object.values(allOutputs).filter((output) => output.videoCompositionId === composition.id);

  return outputs;
}

export async function getDestinationByOutputIds({
  outputIds,
  authContextProvider,
  serviceEnvironment,
}: {
  outputIds: string[];
  authContextProvider: AuthContextProvider;
  serviceEnvironment: ServiceEnvironment;
}) {
  const destinations: Map<string, DestinationMetadataApiClient> = new Map();
  const destinationsApiClient = new DestinationsStorageApiClient({ authContextProvider, serviceEnvironment });
  const allDestinations = await destinationsApiClient.getDestinations();

  for (const destination of allDestinations) {
    if (destination.outputs.some((output) => outputIds.includes(output.id))) {
      destinations.set(
        destination.id,
        new DestinationMetadataApiClient({
          authContextProvider,
          serviceEnvironment,
          destinationMetadata: destination,
        })
      );
      continue;
    }
  }

  return destinations;
}

export async function pointOutputsToVideoCompositionIdByIds({
  outputIds,
  targetCanvasId,
  destinations,
}: {
  outputIds: string[];
  targetCanvasId: string;
  destinations: DestinationMetadataApiClient[];
}) {
  console.log('Pointing outputs to canvas', outputIds, targetCanvasId);

  /** No promise.all because OBS does not do really well with parallel requests xD (in 2024) */
  for (const destination of destinations) {
    await destination.updateDestination({
      outputs: destination.get().outputs.map((output) => ({
        ...output,
        videoCompositionId: outputIds.includes(output.id) ? targetCanvasId : output.videoCompositionId,
      })),
    });
  }

  return destinations;
}

export async function removeVideoComposition({
  input,
  authContextProvider,
  serviceEnvironment,
}: {
  input: CompositionOrCompositionId;
  authContextProvider: AuthContextProvider;
  serviceEnvironment: ServiceEnvironment;
}) {
  await ensureOBSPresence();

  const videoComposition = await getComposition(input);

  if (!videoComposition) {
    throw new Error('The video composition does not exist');
  }

  const outputsUsingComposition = await getOutputsUsingComposition(videoComposition);

  const destinations = await getDestinationByOutputIds({
    outputIds: outputsUsingComposition.map((output) => output.id),
    authContextProvider,
    serviceEnvironment,
  });

  await pointOutputsToVideoCompositionIdByIds({
    outputIds: outputsUsingComposition.map((output) => output.id),
    targetCanvasId: 'default',
    destinations: Array.from(destinations.values()),
  });

  const removedOverlay = await promisefy(window.host.removeVideoCompositionViewOverlay)();

  if (await getIsReplayBufferEnabled()) {
    await ReplayBufferService.suspendExistingReplayBufferForVideoComposition({ videoCompositionId: videoComposition.id });
  }

  const sourceIds: string[] = [];
  const sceneIds: string[] = [];

  for (const scene of videoComposition.scenes) {
    sceneIds.push(scene.id);

    for (const source of scene.sources) {
      sourceIds.push(source.id);
    }
  }

  // 4. Remove composition scenes and sources
  const removedSources = sourceIds.length ? await promisefy(window.host.removeSceneItemsByIds)(sourceIds) : [];
  const removedScenes = sceneIds.length ? await promisefy(window.host.removeScenesByIds)(sceneIds) : [];

  // 3. Remove the canvas from memory
  const removedComposition = await promisefy(window.host.removeVideoCompositionsByIds)([videoComposition.id]);

  return {
    removedOverlay,
    sourcesToRemove: sourceIds,
    scenesToRemove: sceneIds,
    removedSources,
    removedScenes,
    removedComposition,
    affectedDestinations: destinations,
    affectedOutputIds: outputsUsingComposition.map((output) => output.id),
  };
}

class VideoCompositionStorageNotFoundError extends Error {}

export async function loadVideoComposition(params: { videoCompositionId: string; bypassMemoryCheck?: boolean }) {
  await ensureOBSPresence();

  return await navigator.locks.request('VideoCompositionCrudUtils.loadVideoComposition', async (): Promise<VideoCompositionWithScenes> => {
    console.log('--- LOCKED: VideoCompositionCrudUtils.loadVideoComposition: ', params);
    const composition = params.bypassMemoryCheck
      ? null
      : await CompositionsService.getCompositionFromMemory(params.videoCompositionId);

    if (composition) {
      console.info('Composition exists in memory', composition);
      return composition;
    }

    const storedComposition = await CompositionsService.getCompositionFromStorageById(params.videoCompositionId);

    if (!storedComposition) {
      throw new VideoCompositionStorageNotFoundError('Composition does not exist in storage');
    }

    console.info('Composition exists in storage, but not in memory. Registering...');
    const registeredComposition = await CompositionsService.registerComposition(storedComposition.content);

    console.log('Composition registered', registeredComposition);

    if (await getIsReplayBufferEnabled()) {
      await ReplayBufferService.resumeExistingReplayBufferForVideoComposition({ videoCompositionId: params.videoCompositionId });
    }

    return registeredComposition;
  }) as VideoCompositionWithScenes;
}
